import type { TaskSource } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { personenQuery, type LeitstandHeute } from '../../lib/queries'
import { useAufgabenDieserWelt } from '../../lib/welt'
import { AufgabenVerweis } from '../AufgabeKontext'
import { Card, CardPad, SourceBadge } from '../ui/primitives'

type TeamZeile = LeitstandHeute['team']['zeilen'][number]

/** So viele Zeilen stehen offen – wie in „Eingreifen". */
const SICHTBAR = 10

/**
 * **Niemandem zugewiesen** (19.10.2026, [E-415](../../../../../docs/ENTSCHEIDUNGEN.md#e-415)).
 *
 * Nils: *„wichtiger ist, dass wir Aufgaben ermitteln und schnell sehen, die eben aktuell
 * niemandem zugewiesen sind."* Seit E-415 stehen herrenlose, anderen vorgemerkte Aufgaben nicht
 * mehr im Vorlauf aller – also muss die Leitung sie hier finden.
 *
 * Die Menge ist die Zeile „niemand" der Team-Tabelle (ohne Altlasten, ohne Laufendes), damit
 * Karte und Tabelle nicht auseinanderlaufen. Gruppiert nach Frist; ★ zeigt, wer in Frage kommt.
 */
export function NiemandZugewiesen({
  niemand,
  zuweisen,
}: {
  niemand: TeamZeile | undefined
  zuweisen: (id: string, title: string) => void
}) {
  const [alle, setAlle] = useState(false)
  const { aufgaben } = useAufgabenDieserWelt()
  const { data: personen = [] } = useQuery(personenQuery)

  const jeId = new Map(aufgaben.map((a) => [a.id, a]))
  const name = new Map(personen.map((p) => [p.id, p.displayName]))

  const frist = new Set([
    ...(niemand?.ueberfaellig ?? []),
    ...(niemand?.faellig5 ?? []),
    ...(niemand?.ohneDatum ?? []),
  ])
  const gruppen = [
    { titel: 'überfällig', ids: niemand?.ueberfaellig ?? [], ton: 'text-pool-sofort' },
    { titel: 'fällig ≤ 5 Werktage', ids: niemand?.faellig5 ?? [], ton: 'text-[#b25e09]' },
    {
      titel: 'später fällig',
      ids: (niemand?.wartet ?? []).filter((id) => !frist.has(id)),
      ton: 'text-ink2',
    },
    { titel: 'ohne Datum', ids: niemand?.ohneDatum ?? [], ton: 'text-muted' },
  ]
    .map((g) => ({ ...g, zeilen: g.ids.flatMap((id) => jeId.get(id) ?? []) }))
    .filter((g) => g.zeilen.length > 0)

  const gesamt = gruppen.reduce((n, g) => n + g.zeilen.length, 0)
  let rest = alle ? Infinity : SICHTBAR

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[13.5px]">Niemandem zugewiesen</b>
          <span className="text-xs text-muted">
            {gesamt} {gesamt === 1 ? 'Aufgabe' : 'Aufgaben'} · nach Frist · ★ kommt in Frage
          </span>
        </div>

        {gesamt === 0 ? (
          <p className="mt-3 text-[13px] text-ink2">Jede offene Aufgabe hat jemanden.</p>
        ) : (
          gruppen.map((g) => {
            const zeilen = g.zeilen.slice(0, Math.max(rest, 0))
            rest -= zeilen.length
            if (zeilen.length === 0) return null
            return (
              <div key={g.titel} className="mt-2">
                <div className={`text-[11px] font-semibold ${g.ton}`}>
                  {g.titel} · {g.zeilen.length}
                </div>
                <ul>
                  {zeilen.map((a) => {
                    /** `tasks.all` führt „kommen in Frage" als Komma-Liste – wie in `Zeitachse`. */
                    const wunsch = (a.wunschIds ?? '')
                      .split(',')
                      .filter(Boolean)
                      .map((id) => name.get(id) ?? '?')
                    return (
                      <li
                        key={a.id}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 border-t border-line py-1.5 first:border-t-0"
                      >
                        <div className="min-w-0">
                          <AufgabenVerweis
                            aufgabeId={a.id}
                            className="max-w-full cursor-pointer truncate text-left text-[13px] font-semibold hover:underline"
                            title={a.title}
                          >
                            {a.title}
                          </AufgabenVerweis>
                          <div className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-muted">
                            <SourceBadge source={a.sourceId as TaskSource} />
                            {wunsch.length > 0 && (
                              <span className="truncate">★ {wunsch.join(', ')}</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => zuweisen(a.id, a.title)}
                          aria-label={`${a.title}: zuweisen`}
                          className="cursor-pointer rounded-lg border border-accent bg-accent px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-white hover:bg-accent-deep"
                        >
                          Zuweisen
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })
        )}

        {gesamt > SICHTBAR && (
          <button
            type="button"
            onClick={() => setAlle(!alle)}
            className="mt-2 cursor-pointer text-xs font-semibold text-accent-deep hover:underline"
          >
            {alle ? 'weniger zeigen' : `+ ${gesamt - SICHTBAR} weitere`}
          </button>
        )}
      </CardPad>
    </Card>
  )
}
